import { useEffect, useState } from "react";
import api, { API_BASE, formatApiError, resolveAssetUrl } from "@/lib/api";

// Format INR with Indian digit grouping (12,34,56,789).
const inr = (n) => (n == null || n === "") ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/statusUtils";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Eye, Pencil, Download, Trash2, Printer, Upload } from "lucide-react";
import BookingPrintView from "@/components/BookingPrintView";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_STYLES = {
  pending: { bg: "#EAB30820", color: "#B08B00", label: "Pending" },
  approved: { bg: "#34735520", color: "#347355", label: "Approved" },
  rejected: { bg: "#DC262620", color: "#DC2626", label: "Rejected" },
};

const emptyEdit = {
  customer_name: "", customer_mobile: "", customer_email: "", customer_address: "",
  id_reference: "", quoted_price: "", payment_mode: "", amount_paid: "", notes: "", passbook_no: "",
  // Property / boundary correction fields
  area_sqyd: "", sketch_url: "",
  boundaries: { north: "", south: "", east: "", west: "" },
  // Inline status transition
  status: "", rejected_reason: "",
};

export default function AdminBookings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [approveId, setApproveId] = useState(null);
  const [passbookNo, setPassbookNo] = useState("");
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [uploadingEditSketch, setUploadingEditSketch] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings", { params: tab !== "all" ? { status_filter: tab } : {} });
      setBookings(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openEdit = (b) => {
    setEditing(b);
    const bnd = b.boundaries || {};
    setEditForm({
      customer_name: b.customer_name || "", customer_mobile: b.customer_mobile || "",
      customer_email: b.customer_email || "", customer_address: b.customer_address || "",
      id_reference: b.id_reference || "", quoted_price: b.quoted_price ?? "",
      payment_mode: b.payment_mode || "", amount_paid: b.amount_paid ?? "",
      notes: b.notes || "", passbook_no: b.passbook_no || "",
      area_sqyd: b.area_sqyd ?? "",
      sketch_url: b.sketch_url || "",
      boundaries: {
        north: bnd.north || "", south: bnd.south || "",
        east: bnd.east || "", west: bnd.west || "",
      },
      status: b.status || "",
      rejected_reason: "",
    });
  };

  const uploadEditSketch = async (file) => {
    if (!file) return;
    setUploadingEditSketch(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/booking-sketch", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditForm((f) => ({ ...f, sketch_url: data.url }));
      toast.success("Sketch replaced");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingEditSketch(false); }
  };

  const saveEdit = async () => {
    try {
      const boundariesFilled = Object.values(editForm.boundaries).some((x) => (x || "").trim());
      const allFour = ["north", "south", "east", "west"].every((k) => (editForm.boundaries[k] || "").trim());
      if (boundariesFilled && !allFour) {
        toast.error("All four boundaries (N/S/E/W) are required");
        return;
      }
      const payload = {
        customer_name: editForm.customer_name,
        customer_mobile: editForm.customer_mobile,
        customer_email: editForm.customer_email || null,
        customer_address: editForm.customer_address || null,
        id_reference: editForm.id_reference || null,
        payment_mode: editForm.payment_mode || null,
        notes: editForm.notes || null,
        passbook_no: editForm.passbook_no || null,
        quoted_price: editForm.quoted_price !== "" ? parseFloat(editForm.quoted_price) : null,
        amount_paid: editForm.amount_paid !== "" ? parseFloat(editForm.amount_paid) : null,
        area_sqyd: editForm.area_sqyd !== "" ? parseFloat(editForm.area_sqyd) : null,
      };
      // sketch_url: only send when user actually has one — never send blank (backend
      // rejects empty to preserve the "every booking must have a sketch" invariant).
      if ((editForm.sketch_url || "").trim()) {
        payload.sketch_url = editForm.sketch_url;
      }
      if (allFour) payload.boundaries = editForm.boundaries;
      // Only send status if it differs from the current
      if (editForm.status && editForm.status !== editing.status) {
        payload.status = editForm.status;
        if (editForm.status === "rejected") {
          if (!editForm.rejected_reason.trim()) {
            toast.error("Rejection reason is required");
            return;
          }
          payload.rejected_reason = editForm.rejected_reason.trim();
        }
      }
      await api.patch(`/bookings/${editing.id}`, payload);
      toast.success("Booking updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async () => {
    try {
      await api.post(`/bookings/${approveId}/decision`, { action: "approve", passbook_no: passbookNo || null });
      toast.success(passbookNo ? `Approved with passbook #${passbookNo}` : "Booking approved");
      setApproveId(null); setPassbookNo(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async () => {
    try {
      await api.post(`/bookings/${rejectId}/decision`, { action: "reject", reason: rejectReason });
      toast.success("Booking rejected");
      setRejectId(null); setRejectReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (b) => {
    if (!window.confirm(`Delete booking ${b.booking_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Booking deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadExport = () => {
    const params = tab !== "all" ? `?status_filter=${tab}` : "";
    const token = localStorage.getItem("anu_token");
    fetch(`${API_BASE}/export/bookings.xlsx${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "anuttara-bookings.xlsx"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error("Export failed"));
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Admin</div>
            <h1 className="font-heading text-2xl md:text-4xl">Booking Approvals</h1>
          </div>
          <Button onClick={downloadExport} data-testid="export-bookings-btn" variant="outline" className={`rounded-sm w-fit ${user?.role === "super_admin" ? "" : "hidden"}`}>
            <Download className="h-4 w-4 mr-2" /> Export Excel
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm flex-wrap h-auto">
            <TabsTrigger value="pending" data-testid="tab-pending-bookings">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved-bookings">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected-bookings">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all-bookings">All</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-4 border border-[#E5E2DC] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-bookings-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Booking</Th>
                <Th>Plot</Th>
                <Th>Customer</Th>
                <Th>Channel Partner</Th>
                <Th>Price</Th>
                <Th>Passbook</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-12 text-[#5C6653]">Loading…</td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-[#5C6653]">No bookings.</td></tr>
              ) : (
                bookings.map((b) => {
                  const st = STATUS_STYLES[b.status];
                  return (
                    <tr key={b.id} className="border-b border-[#E5E2DC]" data-testid={`admin-booking-row-${b.booking_no}`}>
                      <Td>
                        <span className="font-mono-anu text-xs">{b.booking_no}</span>
                        {b.updated_at && (
                          <span
                            className="ml-1 text-[9px] px-1.5 py-0.5 border font-medium tracking-wider uppercase align-middle"
                            style={{ background: "#EEF2FF", borderColor: "#2C5E7A", color: "#2C5E7A" }}
                            title={`Edited by ${b.updated_by_name || "—"} on ${formatDate(b.updated_at)}`}
                            data-testid={`edited-badge-${b.booking_no}`}
                          >
                            Edited
                          </span>
                        )}
                      </Td>
                      <Td><span className="text-xs text-[#5C6653]">{b.phase_name} · {b.lp_no}</span><br /><span className="font-medium">#{b.plot_no}</span></Td>
                      <Td><div className="font-medium">{b.customer_name}</div><div className="text-xs text-[#5C6653]">{b.customer_mobile}</div></Td>
                      <Td><span className="text-xs">{b.requested_by_name}</span></Td>
                      <Td>{inr(b.quoted_price)}</Td>
                      <Td><span className="font-mono-anu text-xs">{b.passbook_no || "—"}</span></Td>
                      <Td>
                        <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: st.bg, borderColor: st.color, color: st.color }}>
                          {st.label}
                        </span>
                      </Td>
                      <Td><span className="text-xs text-[#5C6653]">{formatDate(b.created_at)}</span></Td>
                      <Td>
                        <div className="flex gap-1.5 flex-wrap">
                          <IconBtn onClick={() => setViewing(b)} title="View" testId={`view-booking-${b.booking_no}`}><Eye className="h-3.5 w-3.5" /></IconBtn>
                          {b.status !== "rejected" && (
                            <IconBtn onClick={() => openEdit(b)} title="Edit booking" testId={`edit-booking-${b.booking_no}`}><Pencil className="h-3.5 w-3.5" /></IconBtn>
                          )}
                          {b.status === "pending" && (
                            <>
                              <IconBtn onClick={() => { setApproveId(b.id); setPassbookNo(""); }} title="Approve" testId={`approve-booking-${b.booking_no}`} tone="primary"><CheckCircle2 className="h-3.5 w-3.5" /></IconBtn>
                              <IconBtn onClick={() => setRejectId(b.id)} title="Reject" testId={`reject-booking-${b.booking_no}`} tone="danger"><XCircle className="h-3.5 w-3.5" /></IconBtn>
                            </>
                          )}
                          {user?.role === "super_admin" && (
                            <IconBtn onClick={() => del(b)} title="Delete" testId={`delete-booking-${b.booking_no}`} tone="danger"><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[92vh] overflow-y-auto w-[calc(100vw-1.5rem)] sm:w-full" data-testid="booking-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex flex-wrap items-center gap-2">
                  {viewing.booking_no}
                  {viewing.is_partial && (
                    <span
                      className="text-[10px] px-2 py-0.5 border font-medium tracking-wider uppercase"
                      style={{ background: "#F4E6D9", borderColor: "#C06C47", color: "#8B4A2B" }}
                      data-testid="booking-partial-badge"
                    >
                      Partial Plot
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription className="sr-only">Booking details for {viewing.booking_no}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1 text-sm max-h-[70vh] overflow-y-auto">
                <SectionHeader>Plot</SectionHeader>
                <Row k="Phase / LP" v={`${viewing.phase_name} · ${viewing.lp_no}`} />
                <Row k="Plot" v={`#${viewing.plot_no}`} />
                {viewing.portion_code && (
                  <Row
                    k="Portion Code"
                    v={<span className="font-mono-anu text-xs" data-testid="booking-portion-code">{viewing.portion_code}</span>}
                  />
                )}
                {viewing.direction && (
                  <Row k="Portion Direction" v={<span data-testid="booking-portion-direction">{directionLabel(viewing.direction)}</span>} />
                )}
                {viewing.area_sqyd != null && (
                  <Row k="Portion Area" v={<span data-testid="booking-portion-area">{Number(viewing.area_sqyd).toFixed(2)} sq.yd</span>} />
                )}
                {viewing.passbook_no && <Row k="Passbook #" v={viewing.passbook_no} />}

                {viewing.boundaries && Object.values(viewing.boundaries).some((x) => x) && (
                  <>
                    <SectionHeader>Boundaries</SectionHeader>
                    <Row k="North" v={<span data-testid="booking-boundary-north">{viewing.boundaries.north || "—"}</span>} />
                    <Row k="South" v={<span data-testid="booking-boundary-south">{viewing.boundaries.south || "—"}</span>} />
                    <Row k="East" v={<span data-testid="booking-boundary-east">{viewing.boundaries.east || "—"}</span>} />
                    <Row k="West" v={<span data-testid="booking-boundary-west">{viewing.boundaries.west || "—"}</span>} />
                  </>
                )}

                {viewing.sketch_url && (
                  <>
                    <SectionHeader>Survey Sketch</SectionHeader>
                    <SketchPreview url={viewing.sketch_url} />
                  </>
                )}

                <SectionHeader>Customer</SectionHeader>
                <Row k="Name" v={viewing.customer_name} />
                <Row k="Mobile" v={viewing.customer_mobile} />
                <Row k="Email" v={viewing.customer_email || "—"} />
                <Row k="Address" v={viewing.customer_address || "—"} />
                <Row k="ID Reference" v={viewing.id_reference || "—"} />

                <SectionHeader>Payment</SectionHeader>
                <Row k="Quoted Price" v={inr(viewing.quoted_price)} />
                <Row k="Amount Paid" v={inr(viewing.amount_paid)} />
                <Row k="Payment Mode" v={viewing.payment_mode || "—"} />
                <Row k="Payment Option" v={(viewing.payment_option || "full").toUpperCase()} />

                <SectionHeader>Meta</SectionHeader>
                <Row k="Notes" v={viewing.notes || "—"} />
                <Row k="Requested By" v={viewing.requested_by_name} />
                <Row k="Status" v={viewing.status} />
                <Row k="Created" v={formatDate(viewing.created_at)} />
                {viewing.rejected_reason && <Row k="Rejection Reason" v={viewing.rejected_reason} />}
                {viewing.updated_at && viewing.updated_by_name && (
                  <Row
                    k="Last Edited"
                    v={
                      <span data-testid="booking-last-edited" className="text-xs">
                        By <b>{viewing.updated_by_name}</b>
                        {viewing.updated_by_role ? ` (${viewing.updated_by_role.replace("_", " ")})` : ""}
                        {" · "}{formatDate(viewing.updated_at)}
                      </span>
                    }
                  />
                )}
                {viewing.stale_flag && (
                  <Row k="⚠ Stale flag" v={<span className="text-[#DC2626]">{viewing.stale_reason || "Marked stale by system"}</span>} />
                )}
              </div>
              {viewing.status === "approved" && (
                <div className="pt-3 border-t border-[#E5E2DC] flex justify-end">
                  <button
                    type="button"
                    onClick={() => window.print()}
                    data-testid="print-booking-btn"
                    className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white text-sm h-9 px-4 inline-flex items-center gap-2"
                  >
                    <Printer className="h-4 w-4" /> Print
                  </button>
                </div>
              )}
              {viewing.status === "approved" && <BookingPrintView booking={viewing} />}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[92vh] overflow-y-auto w-[calc(100vw-1.5rem)] sm:w-full">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Edit booking · {editing.booking_no}</DialogTitle>
                <DialogDescription className="sr-only">Correct details for this booking. Boundaries, area, sketch, and status can be amended by Admin / Super Admin.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="edit-booking-form">
                {/* Customer */}
                <div>
                  <SectionHeader>Customer</SectionHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {[
                      ["customer_name", "Customer Name"],
                      ["customer_mobile", "Mobile"],
                      ["customer_email", "Email"],
                      ["id_reference", "ID Reference (Aadhaar/PAN)"],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <Label className="text-xs font-medium mb-1 block">{label}</Label>
                        <Input value={editForm[key]} onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })} data-testid={`edit-${key}`} className="rounded-sm" />
                      </div>
                    ))}
                    <div className="md:col-span-2">
                      <Label className="text-xs font-medium mb-1 block">Address</Label>
                      <Textarea value={editForm.customer_address} onChange={(e) => setEditForm({ ...editForm, customer_address: e.target.value })} data-testid="edit-address" rows={2} className="rounded-sm" />
                    </div>
                  </div>
                </div>

                {/* Payment */}
                <div>
                  <SectionHeader>Payment</SectionHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {[
                      ["quoted_price", "Quoted Price (₹)"],
                      ["amount_paid", "Amount Paid (₹)"],
                      ["payment_mode", "Payment Mode"],
                      ["passbook_no", "Passbook #"],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <Label className="text-xs font-medium mb-1 block">{label}</Label>
                        <Input value={editForm[key]} onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })} data-testid={`edit-${key}`} className="rounded-sm" />
                      </div>
                    ))}
                    <div className="md:col-span-2">
                      <Label className="text-xs font-medium mb-1 block">Notes</Label>
                      <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-notes" rows={2} className="rounded-sm" />
                    </div>
                  </div>
                </div>

                {/* Property / Boundary / Sketch */}
                <div>
                  <SectionHeader>Property, Boundaries &amp; Sketch</SectionHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-xs font-medium mb-1 block">Area (sq.yd)</Label>
                      <Input
                        type="number" step="0.01"
                        value={editForm.area_sqyd}
                        onChange={(e) => setEditForm({ ...editForm, area_sqyd: e.target.value })}
                        data-testid="edit-area-sqyd"
                        className="rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium mb-1 block">Direction</Label>
                      <Input readOnly value={directionLabel(editing.direction) || "—"} className="rounded-sm bg-[#F9F8F6]" data-testid="edit-direction-readonly" />
                    </div>
                    {["north", "south", "east", "west"].map((side) => (
                      <div key={side}>
                        <Label className="text-xs font-medium mb-1 block">Boundary — {side.charAt(0).toUpperCase()}{side.slice(1)}</Label>
                        <Input
                          value={editForm.boundaries[side]}
                          onChange={(e) => setEditForm({ ...editForm, boundaries: { ...editForm.boundaries, [side]: e.target.value } })}
                          data-testid={`edit-boundary-${side}`}
                          className="rounded-sm"
                          placeholder="Adjacent to…"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs font-medium mb-1 block">Survey sketch</Label>
                    {editForm.sketch_url ? (
                      <div className="flex items-center gap-2 border border-[#E5E2DC] bg-white px-3 py-2 text-xs rounded-sm">
                        <CheckCircle2 className="h-4 w-4 text-[#347355]" />
                        <a href={resolveAssetUrl(editForm.sketch_url)} target="_blank" rel="noreferrer" className="text-[#234B36] underline truncate flex-1" data-testid="edit-sketch-link">
                          Current sketch
                        </a>
                        <label className="inline-flex items-center gap-1 border border-[#E5E2DC] px-2 py-1 rounded-sm cursor-pointer hover:bg-[#F0EFEA]" data-testid="edit-sketch-replace">
                          <Upload className="h-3 w-3" />
                          <span>{uploadingEditSketch ? "…" : "Replace"}</span>
                          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadEditSketch(e.target.files?.[0])} />
                        </label>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 border border-dashed border-[#C06C47]/50 bg-white px-3 py-2.5 text-xs rounded-sm cursor-pointer hover:bg-[#F9F8F6]" data-testid="edit-sketch-dropzone">
                        <Upload className="h-4 w-4 text-[#C06C47]" />
                        <span className="text-[#5C6653]">{uploadingEditSketch ? "Uploading…" : "Click to upload sketch"}</span>
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadEditSketch(e.target.files?.[0])} data-testid="edit-sketch-input" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Status transition (inline approve / reject / revert) */}
                <div>
                  <SectionHeader>Status</SectionHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-xs font-medium mb-1 block">Current status</Label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        data-testid="edit-status"
                        className="w-full h-9 border border-[#E5E2DC] rounded-sm px-2 text-sm bg-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <p className="text-[10px] text-[#5C6653] mt-1">
                        Changing to <b>Approved</b> uses the Passbook # above (if set, plot becomes Sold; else Booked).
                      </p>
                    </div>
                    {editForm.status === "rejected" && editing.status !== "rejected" && (
                      <div>
                        <Label className="text-xs font-medium mb-1 block">Rejection reason <span className="text-[#C06C47]">*</span></Label>
                        <Textarea
                          rows={2}
                          value={editForm.rejected_reason}
                          onChange={(e) => setEditForm({ ...editForm, rejected_reason: e.target.value })}
                          data-testid="edit-rejected-reason"
                          className="rounded-sm"
                          placeholder="Reason for rejection…"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={saveEdit} data-testid="save-edit-booking" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">Save changes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve with passbook */}
      <Dialog open={!!approveId} onOpenChange={(o) => !o && setApproveId(null)}>
        <DialogContent className="max-w-md rounded-sm w-[calc(100vw-1.5rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Approve booking</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs font-medium mb-1 block">Passbook # (optional)</Label>
            <Input value={passbookNo} onChange={(e) => setPassbookNo(e.target.value)} placeholder="e.g. PB-2026-0001" data-testid="passbook-input" className="rounded-sm" />
            <p className="text-xs text-[#5C6653] mt-2">If a <b>Passbook #</b> is entered, the plot will be marked <b>Sold</b>. Without a passbook number the plot becomes <b>Booked / Reserved</b> and can be marked sold later from the plot editor.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveId(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={approve} data-testid="confirm-approve-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject */}
      <Dialog open={!!rejectId} onOpenChange={(o) => !o && setRejectId(null)}>
        <DialogContent className="max-w-md rounded-sm w-[calc(100vw-1.5rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Reject booking</DialogTitle>
          </DialogHeader>
          <div>
            <Textarea placeholder="Reason for rejection…" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input" className="rounded-sm" rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={reject} data-testid="confirm-reject-btn" className="rounded-sm bg-red-700 hover:bg-red-800 text-white">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1 border-b border-[#E5E2DC] last:border-0">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold col-span-1 self-center">{k}</div>
      <div className="col-span-2 text-sm">{v}</div>
    </div>
  );
}
function SectionHeader({ children }) {
  return (
    <div className="mt-3 mb-1 text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold border-b border-[#C06C47]/20 pb-1">
      {children}
    </div>
  );
}
const _DIR_LBL = {
  F: "Full plot", N: "North", S: "South", E: "East", W: "West",
  NE: "North-East", NW: "North-West", SE: "South-East", SW: "South-West",
  P1: "Portion 1", P2: "Portion 2", P3: "Portion 3", P4: "Portion 4",
};
function directionLabel(d) { return _DIR_LBL[d] || d; }
function SketchPreview({ url }) {
  const absolute = resolveAssetUrl(url);
  const isPdf = /\.pdf($|\?)/i.test(url);
  return (
    <div className="py-2" data-testid="booking-sketch-preview">
      {isPdf ? (
        <a href={absolute} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#234B36] underline underline-offset-4 hover:text-[#1A3A29]" data-testid="booking-sketch-link">
          <Download className="h-4 w-4" /> Open sketch PDF
        </a>
      ) : (
        <a href={absolute} target="_blank" rel="noreferrer" className="block" data-testid="booking-sketch-link">
          <img
            src={absolute}
            alt="Survey sketch"
            className="max-h-64 max-w-full border border-[#E5E2DC] rounded-sm hover:opacity-90 transition-opacity"
          />
          <div className="text-[10px] text-[#5C6653] mt-1">Click image to open full-size</div>
        </a>
      )}
    </div>
  );
}
function IconBtn({ children, onClick, title, testId, tone }) {
  const toneCls = tone === "primary" ? "bg-[#234B36] hover:bg-[#1A3A29] text-white border-[#234B36]"
    : tone === "danger" ? "border-red-300 text-red-700 hover:bg-red-50"
    : "border-[#E5E2DC] hover:bg-[#F0EFEA]";
  return (
    <button onClick={onClick} data-testid={testId} title={title} className={`h-8 w-8 flex items-center justify-center border rounded-sm ${toneCls}`}>
      {children}
    </button>
  );
}
